// QuizApp.tsx
import React, { useState } from 'react';
import QuestionCard from './QuestionCard';
import { QuestionData, Option } from './types';

interface QuizAppProps {
    qTitle: string,
    questions: QuestionData[];
}

const QuizApp: React.FC<QuizAppProps> = ({ qTitle, questions }) => {
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
    // Store answers: for single, it's string | undefined; for multiple, it's string[]
    const [userAnswers, setUserAnswers] = useState<Record<number, string | string[] | undefined>>({});
    const [showResults, setShowResults] = useState<boolean>(false);
    const [isPracticeMode, setIsPracticeMode] = useState<boolean>(true);
    const [feedbackResetKey, setFeedbackResetKey] = useState<number>(0);
    const [questionInput, setQuestionInput] = useState<string>('');

    const totalQuestions = questions.length;
    const currentQuestion = questions[currentQuestionIndex];
    currentQuestion.type = currentQuestion.answer.includes(',') && currentQuestion.answer.length <= 10 ? 'multiple' : 'single';
    // Helper to parse correct answers from the `answer` field (string or array)
    // This is a duplicate from QuestionCard, consider putting it in a utility file
    // or passing it down. For now, keeping it here as well for score calculation.
    const parseAnswerToValues = (answer: string | string[], options: Option[]): string[] => {
        if (Array.isArray(answer)) {
            return answer.map(ans => ans.trim());
        } else if (typeof answer === 'string' && answer.length <= 10) {
            const answers = answer.split(',').map(ans => ans.trim());
            return answers.map(ans => {
                const option = options.find(opt => opt.option_no === ans);
                return option && option.value !== undefined ? option.value : ans;
            });
        } else {
            const option = options.find(opt => opt.option_no.trim() === answer.trim());
            return option && option.value !== undefined ? [option.value] : [answer];
        }
        return [];
    };

    const navigateToQuestion = (index: number) => {
        if (index >= 0 && index < totalQuestions) {
            setCurrentQuestionIndex(index);
            setFeedbackResetKey(prev => prev + 1);
            setQuestionInput('');
        } else {
            alert(`Please enter a question number between 1 and ${totalQuestions}.`);
        }
    };

    const handleNext = () => {
        if (currentQuestionIndex < totalQuestions - 1) {
            navigateToQuestion(currentQuestionIndex + 1);
        } else {
            setShowResults(true);
        }
    };

    const handlePrevious = () => {
        navigateToQuestion(currentQuestionIndex - 1);
    };

    const handleGoToQuestion = () => {
        const qNum = parseInt(questionInput, 10);
        if (!isNaN(qNum) && qNum >= 1 && qNum <= totalQuestions) {
            navigateToQuestion(qNum - 1); // Adjust to 0-based index
        } else {
            alert(`Please enter a valid number between 1 and ${totalQuestions}.`);
        }
    };

    const handleSelectAnswer = (
        qNo: number,
        selectedValue: string | undefined,
        isChecked?: boolean
    ) => {
        setUserAnswers(prevAnswers => {
            const question = questions.find(q => q.qNo === qNo);
            if (!question) return prevAnswers;

            if (question.type === 'multiple') {
                // Ensure currentSelections is always an array for 'multiple' type
                let currentSelections: string[] = Array.isArray(prevAnswers[qNo])
                    ? (prevAnswers[qNo] as string[])
                    : [];

                // Determine the actual value to store (prefer option.value if available)
                const selectedOption = question.options.find(opt => opt.value === selectedValue || opt.option_no === selectedValue);
                const valueToStore = selectedOption?.value !== undefined ? selectedOption.value : selectedOption?.option_no;


                if (valueToStore !== undefined) {
                    if (isChecked) {
                        if (!currentSelections.includes(valueToStore)) {
                            currentSelections = [...currentSelections, valueToStore];
                        }
                    } else {
                        currentSelections = currentSelections.filter(val => val !== valueToStore);
                    }
                }
                return {
                    ...prevAnswers,
                    [qNo]: currentSelections,
                };
            } else {
                // Single answer (radio) - store as string or undefined
                return {
                    ...prevAnswers,
                    [qNo]: selectedValue,
                };
            }
        });
    };


    const calculateScore = () => {
        let score = 0;
        questions.forEach(question => {
            const userAnswer = userAnswers[question.qNo];
            const correctAnswersArray = parseAnswerToValues(question.answer, question.options);

            if (question.type === 'single') {
                // Compare single user answer (string) with the first correct answer value
                if (userAnswer?.toString().toLowerCase() === correctAnswersArray[0].toLowerCase()) {
                    score++;
                }
            } else {
                // Multiple answer (checkboxes)
                // Ensure userAnswer is an array for comparison
                const userSelectedArray = Array.isArray(userAnswer) ? userAnswer : [];

                // Check if all correct answers are present in user's selection AND
                // user's selection contains *only* correct answers (no extraneous wrong ones)
                const isCorrect =
                    correctAnswersArray.every(ans => userSelectedArray.every(userAnswer => userAnswer.toLowerCase().includes(ans.toLowerCase()))) &&
                    userSelectedArray.every(ans => correctAnswersArray.every(correctAnswer => correctAnswer.toLowerCase().includes(ans.toLowerCase())));

                if (isCorrect) {
                    score++;
                }
            }
        });
        return score;
    };

    const restartQuiz = () => {
        setCurrentQuestionIndex(0);
        setUserAnswers({});
        setShowResults(false);
        setIsPracticeMode(true);
        setFeedbackResetKey(0);
        setQuestionInput('');
    };
    const resetQuiz = () => {
        window.location.href = "/";
    }

    if (showResults) {
        const score = calculateScore();
        return (
            <div className="quiz-results">
                <h3>Quiz Results</h3>
                <p>You scored {score} out of {totalQuestions} questions correctly!</p>
                <button onClick={restartQuiz} className="quiz-button">Restart Quiz</button>
                &nbsp;&nbsp;
                <button onClick={resetQuiz} className="quiz-button">Reset Quiz</button>
                <div className="review-answers">
                    <h3>Review Your Answers:</h3>
                    {questions.map(question => {
                        const userAnswer = userAnswers[question.qNo];
                        const correctAnswersArray = parseAnswerToValues(question.answer, question.options);

                        const isCorrect = question.type === 'single'
                            ? userAnswer?.toString().toLowerCase() === correctAnswersArray[0].toLowerCase()
                            : (Array.isArray(userAnswer) &&
                                correctAnswersArray.every(ans => userAnswer.every(uAns => uAns.toLowerCase().includes(ans.toLowerCase()))) &&
                                userAnswer.every(ans => correctAnswersArray.every(correctAnswer => correctAnswer.toLowerCase().includes(ans.toLowerCase()))));

                        const displayUserAnswer = Array.isArray(userAnswer)
                            ? (userAnswer.length > 0 ? userAnswer.join(', ') : 'Not answered')
                            : (userAnswer || 'Not answered');

                        return (
                            <div key={question.qNo} className="review-item">
                                <p><strong>Q{question.qNo}:</strong> {question.question}</p>
                                <p>Your Answer: {displayUserAnswer}</p>
                                <p className={isCorrect ? 'correct-answer' : 'wrong-answer'}>
                                    Correct Answer: {correctAnswersArray.join(', ')}
                                </p>
                                {question.explanation && (
                                    <p>
                                        <strong>Explanation:</strong> {question.explanation}
                                    </p>
                                )}
                                {question.reference && (
                                    <p>
                                        <strong>Reference:</strong>{' '}
                                        {question.reference.startsWith('http') ? (
                                            <a href={question.reference} target="_blank" rel="noopener noreferrer">
                                                {question.reference}
                                            </a>
                                        ) : (
                                            question.reference
                                        )}
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    return (
        <div className="quiz-container">
            <h2>{qTitle || "Dynamic Quiz"}</h2>
            <div className="mode-toggle">
                {/* <label>
                    <input
                        type="checkbox"
                    //checked={isPracticeMode}
                    //onChange={() => setIsPracticeMode(!isPracticeMode)}
                    />
                    Set 1
                </label>
                <label>
                    <input
                        type="checkbox"
                    // checked={isPracticeMode}
                    // onChange={() => setIsPracticeMode(!isPracticeMode)}
                    />
                    Set 2
                </label> */}
                <label>
                    <input
                        type="checkbox"
                        checked={isPracticeMode}
                        onChange={() => setIsPracticeMode(!isPracticeMode)}
                    />
                    Practice Mode
                </label>
            </div>

            {totalQuestions === 0 ? (
                <p>No questions available.</p>
            ) : (
                <>
                    <p className="question-counter">Question {currentQuestionIndex + 1} of {totalQuestions}</p>
                    <QuestionCard
                        questionData={currentQuestion}
                        isPracticeMode={isPracticeMode}
                        // Ensure selectedAnswer is correctly typed for the current question type
                        selectedAnswer={
                            currentQuestion.type === 'single'
                                ? (userAnswers[currentQuestion.qNo] as string | undefined)
                                : (userAnswers[currentQuestion.qNo] as string[] || []) // Always pass an array for multiple
                        }
                        onSelectAnswer={handleSelectAnswer}
                        resetFeedback={feedbackResetKey}
                    />
                    <div className="quiz-navigation">
                        <button
                            onClick={handlePrevious}
                            disabled={currentQuestionIndex === 0}
                            className="quiz-button"
                        >
                            Previous
                        </button>
                        <div className="manual-navigation">
                            <label htmlFor="qNumInput">Go to QNo:</label>
                            <input
                                type="number"
                                id="qNumInput"
                                min="1"
                                max={totalQuestions}
                                value={questionInput}
                                onChange={(e) => setQuestionInput(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handleGoToQuestion();
                                    }
                                }}
                            />
                            <button onClick={handleGoToQuestion} className="quiz-button go-button">
                                Go
                            </button>
                        </div>
                        <button
                            onClick={() => {
                                if (currentQuestionIndex === totalQuestions - 1) {
                                    setShowResults(true); // Last question, show results
                                } else {
                                    handleNext(); // Otherwise, go to next question
                                }
                            }}
                            className="quiz-button"
                        >
                            {currentQuestionIndex === totalQuestions - 1 ? 'Finish Quiz' : 'Next'}
                        </button>
                    </div>
                </>
            )}
            <br />
        </div>
    );
};

export default QuizApp;